import { unref } from 'vue';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export function toDefinedString(v: unknown): string | undefined {
  const val = unref(v);
  if (typeof val !== 'string') return undefined; // narrow: no objects, no numbers
  const s = val.trim();
  return s.length ? s : undefined;
}

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const formatIban = (iban: string): string => {
  if (!iban) return '';
  return iban.replace(/(.{4})/g, '$1 ').trim();
};

const clusterSubscriberNumber = (number: string): string => {
  const parts: string[] = [];

  let i = 0;
  const chunksize = 3;
  while (i < number.length) {
    const mod = i % chunksize;
    if (mod === 0) {
      parts.push(number.slice(i, i + chunksize));
    }
    i++;
  }

  // parts is a string[] – not optional
  const len = parts.length;
  if (len >= 2) {
    const last = parts[len - 1]; // last exists

    if (last?.length === 1) {
      // Build combined string from last two items
      const allRest = parts[len - 2] + last;

      // Split back into two parts
      parts[len - 2] = allRest.slice(0, 2);
      parts[len - 1] = allRest.slice(2);
    }
  }

  return parts.join(' ');
};

export const formatInternationalPhoneNumber = (input: string): string => {
  if (!input) return '';

  const cleaned = input.replace(/[\s\-().]/g, '');

  // Default-country = germany (DE)
  const phoneNumber = parsePhoneNumberFromString(cleaned, 'DE');
  const intPhoneNumber = phoneNumber ? phoneNumber.formatInternational() : input;

  const parts = intPhoneNumber.trim().split(' ');
  let countryCode;
  let areaCode;
  let subscriberParts;
  if (parts.length === 3) {
    [countryCode, areaCode, ...subscriberParts] = parts;
  } else {
    areaCode = '';
    [countryCode, ...subscriberParts] = parts;
  }
  const subscriberRaw = subscriberParts.join('');
  const clustered = clusterSubscriberNumber(subscriberRaw);

  return `${countryCode} ${areaCode} ${clustered}`.replace(/\s+/g, ' ').trim();
};

export interface StringifyOptions {
  arraySeparator?: string; // default: ", "
  maxArrayItems?: number; // optional limit
  dateToISO?: boolean; // Date as ISO, else locale
}

export function safeStringify(value: unknown, opts: StringifyOptions = {}): string | undefined {
  const { arraySeparator = ', ', maxArrayItems, dateToISO = true } = opts;

  if (value == null) return undefined;

  // primitive
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint') {
    return String(value);
  }

  // Date
  if (value instanceof Date) {
    return dateToISO ? value.toISOString() : value.toString();
  }

  // Array
  if (Array.isArray(value)) {
    const arr = maxArrayItems ? value.slice(0, maxArrayItems) : value;
    return arr
      .map((v) =>
        typeof v === 'string'
          ? v
          : typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint'
            ? String(v)
            : // non-strings in arrays: fallback to JSON
              safeJson(v),
      )
      .join(arraySeparator);
  }

  // object: JSON instead implicit [object Object]
  return safeJson(value);
}

export function safeJson(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return Object.prototype.toString.call(v);
  }
}

/**
 * Converts any value safely to a string.
 * Avoids "[object Object]" from plain String() conversion.
 */
export function safeToString(value: unknown, fallback = ''): string {
  if (value == null) return fallback;

  switch (typeof value) {
    case 'string':
      return value.trim();
    case 'number':
    case 'boolean':
      return String(value);
    case 'bigint':
      return value.toString();
    case 'symbol':
      return value.description ?? fallback;
    case 'object':
      if (value instanceof Date) return value.toISOString();
      try {
        return JSON.stringify(value);
      } catch {
        return fallback;
      }
    default:
      return fallback;
  }
}
